import { useState, useEffect } from "react";
import { Pill, Plus, Edit2, Trash2, Copy, Package, Search } from "lucide-react";
import { Preset, PresetGroup } from "../lib/supabase";
import {
  getBuiltInPresets,
  getUserPresets,
  deletePreset,
  duplicatePreset,
  getGroupsForPreset,
  getUserGroups,
  deleteGroup,
} from "../lib/api";
import { formatFoodRelation } from "../lib/scheduler";
import { PresetEditor } from "./PresetEditor";
import { GroupEditor } from "./GroupEditor";

export function PresetsList() {
  const [activeTab, setActiveTab] = useState<"builtin" | "mine" | "groups">(
    "builtin"
  );
  const [builtInPresets, setBuiltInPresets] = useState<Preset[]>([]);
  const [userPresets, setUserPresets] = useState<Preset[]>([]);
  const [groups, setGroups] = useState<(PresetGroup & { presets: Preset[] })[]>(
    []
  );
  const [presetGroups, setPresetGroups] = useState<Map<string, PresetGroup[]>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPreset, setEditingPreset] = useState<Preset | null>(null);
  const [creatingPreset, setCreatingPreset] = useState(false);
  const [editingGroup, setEditingGroup] = useState<
    (PresetGroup & { presets: Preset[] }) | null
  >(null);
  const [creatingGroup, setCreatingGroup] = useState(false);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    setLoading(true);
    try {
      const [builtin, user, userGroups] = await Promise.all([
        getBuiltInPresets(),
        getUserPresets(),
        getUserGroups(),
      ]);
      setBuiltInPresets(builtin);
      setUserPresets(user);
      setGroups(userGroups);

      const allPresets = [...builtin, ...user];
      const groupsMap = new Map<string, PresetGroup[]>();

      await Promise.all(
        allPresets.map(async (preset) => {
          const groups = await getGroupsForPreset(preset.id);
          if (groups.length > 0) {
            groupsMap.set(preset.id, groups);
          }
        })
      );

      setPresetGroups(groupsMap);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm("Delete this preset?")) {
      await deletePreset(id);
      await loadPresets();
    }
  };

  const handleDuplicate = async (preset: Preset) => {
    await duplicatePreset(preset.id);
    await loadPresets();
    setActiveTab("mine");
  };

  const handleDeleteGroup = async (id: string) => {
    if (
      confirm("Delete this group? Presets in the group will not be deleted.")
    ) {
      await deleteGroup(id);
      await loadPresets();
    }
  };

  const presets = activeTab === "builtin" ? builtInPresets : userPresets;
  const filteredPresets = presets.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = groups.filter(
    (g) =>
      g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.presets.some((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  if (editingGroup || creatingGroup) {
    return (
      <GroupEditor
        group={editingGroup}
        onSave={async () => {
          await loadPresets();
          setEditingGroup(null);
          setCreatingGroup(false);
        }}
        onCancel={() => {
          setEditingGroup(null);
          setCreatingGroup(false);
        }}
      />
    );
  }

  if (editingPreset || creatingPreset) {
    return (
      <PresetEditor
        preset={editingPreset}
        onSave={async () => {
          await loadPresets();
          setEditingPreset(null);
          setCreatingPreset(false);
          if (editingPreset?.owner_user_id === null) {
            setActiveTab("mine");
          }
        }}
        onCancel={() => {
          setEditingPreset(null);
          setCreatingPreset(false);
        }}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 safe-area-x animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-warm-dark mb-4">
          Presets Library
        </h1>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab("builtin")}
            className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md active:scale-95 ${
              activeTab === "builtin"
                ? "bg-warm-primary text-white warm-shadow"
                : "bg-warm-glass text-warm-dark hover:bg-warm-hover border border-warm"
            }`}
          >
            Built-in ({builtInPresets.length})
          </button>
          <button
            onClick={() => setActiveTab("mine")}
            className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md active:scale-95 ${
              activeTab === "mine"
                ? "bg-warm-primary text-white warm-shadow"
                : "bg-warm-glass text-warm-dark hover:bg-warm-hover border border-warm"
            }`}
          >
            My Presets ({userPresets.length})
          </button>
          <button
            onClick={() => setActiveTab("groups")}
            className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md active:scale-95 flex items-center justify-center gap-2 ${
              activeTab === "groups"
                ? "bg-warm-primary text-white warm-shadow"
                : "bg-warm-glass text-warm-dark hover:bg-warm-hover border border-warm"
            }`}
          >
            <Package size={18} />
            Groups
          </button>
        </div>

        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder={
              activeTab === "groups"
                ? "Search groups..."
                : "Search by name or tag..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-warm rounded-lg focus:ring-2 focus:ring-warm-primary focus:border-warm-solid focus:scale-[1.01] transition-all duration-200 bg-warm-glass"
          />
        </div>
      </div>

      {activeTab === "mine" && (
        <button
          onClick={() => setCreatingPreset(true)}
          className="w-full mb-4 bg-warm-primary text-white py-3 rounded-lg font-medium hover:bg-warm-hover hover:warm-glow active:scale-95 flex items-center justify-center gap-2 transition-all duration-200 warm-shadow"
        >
          <Plus size={20} />
          Create New Preset
        </button>
      )}

      {activeTab === "groups" && (
        <button
          onClick={() => setCreatingGroup(true)}
          className="w-full mb-4 bg-warm-primary text-white py-3 rounded-lg font-medium hover:bg-warm-hover hover:warm-glow flex items-center justify-center gap-2 active:scale-95 transition-all duration-200 warm-shadow"
        >
          <Plus size={20} />
          Create New Group
        </button>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-600">Loading...</div>
      ) : activeTab === "groups" ? (
        filteredGroups.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            {searchTerm
              ? "No groups found"
              : "No groups yet. Create your first group above."}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredGroups.map((group, index) => (
              <div
                key={group.id}
                className="bg-warm-card rounded-xl border border-warm p-4 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 animate-slide-in warm-shadow"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start gap-3">
                  <div className="bg-warm-light rounded-lg p-3 flex-shrink-0">
                    <Package className="text-warm-secondary" size={24} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg text-warm-dark mb-1">
                      {group.name}
                    </h3>

                    {group.description && (
                      <p className="text-sm text-gray-700 mb-2">
                        {group.description}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs bg-warm-light text-warm-secondary px-2 py-1 rounded font-medium">
                        {group.presets.length} supplement
                        {group.presets.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="space-y-1">
                      {group.presets.map((preset) => (
                        <div
                          key={preset.id}
                          className="text-sm text-gray-700 flex items-center gap-2"
                        >
                          <span className="w-1.5 h-1.5 bg-warm-primary rounded-full flex-shrink-0"></span>
                          <span>{preset.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => setEditingGroup(group)}
                      className="p-2 text-warm-dark hover:bg-warm-light hover:scale-110 rounded-lg active:scale-95 transition-all duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label="Edit group"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id)}
                      className="p-2 text-red-600 hover:bg-red-50 hover:scale-110 rounded-lg active:scale-95 transition-all duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label="Delete group"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : filteredPresets.length === 0 ? (
        <div className="text-center py-12 text-gray-600">
          {searchTerm ? "No presets found" : "No presets yet"}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPresets.map((preset, index) => (
            <div
              key={preset.id}
              className="bg-warm-card rounded-lg border border-warm p-4 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 animate-slide-in warm-shadow"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <div className="flex items-start gap-3">
                <div className="bg-warm-light rounded-lg p-2">
                  <Pill className="text-warm-secondary" size={24} />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-warm-dark">
                    {preset.name}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="text-xs bg-warm-glass text-warm-dark px-2 py-1 rounded border border-warm">
                      {preset.tag}
                    </span>
                    <span className="text-xs bg-warm-light text-warm-secondary px-2 py-1 rounded">
                      Priority {preset.priority}
                    </span>
                    <span className="text-xs bg-gradient-to-r from-amber-100 to-orange-100 text-warm-secondary px-2 py-1 rounded">
                      {formatFoodRelation(preset.food_relation)}
                    </span>
                  </div>
                  {preset.description && (
                    <p className="text-sm text-gray-700 mt-2">
                      {preset.description}
                    </p>
                  )}
                  {presetGroups.has(preset.id) && (
                    <div className="mt-2 flex items-center gap-1 flex-wrap">
                      <Package size={12} className="text-warm-secondary" />
                      <span className="text-xs text-warm-secondary font-medium">
                        In {presetGroups.get(preset.id)!.length} group
                        {presetGroups.get(preset.id)!.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  {activeTab === "builtin" ? (
                    <button
                      onClick={() => handleDuplicate(preset)}
                      className="p-2 text-warm-secondary hover:bg-warm-light hover:scale-110 rounded-lg transition-all duration-200 active:scale-95"
                      title="Duplicate to My Presets"
                    >
                      <Copy size={18} />
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditingPreset(preset)}
                        className="p-2 text-warm-dark hover:bg-warm-light hover:scale-110 rounded-lg transition-all duration-200 active:scale-95"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(preset.id)}
                        className="p-2 text-red-600 hover:bg-red-50 hover:scale-110 rounded-lg transition-all duration-200 active:scale-95"
                      >
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
